import { Address } from '@/Domain/Entities';
import { Constants, InscriptionModules } from '@/Domain/enum';
import MasterDataService from '@/Services/MasterDataService';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { MdSelect, MdInput } from '@/Components';

@Component({
    name: 'address-section',
    components: { MdSelect, MdInput }
})
export default class AddressSection extends Vue {
    @Prop() readonly isEditMode!: boolean;
    @Prop() readonly isDraftMode!: boolean;
    @Prop() readonly isAdminRole!: boolean;
    @Prop() readonly isAnyModuleEditing!: boolean;
    @Prop() readonly address!: Address;
    @Prop() readonly streetTypes!: any;

    loadingStreetTypes: boolean = false;
    Constants = Constants;
    countries: Array<{id: number, name: string}> = [];
    provinces: object = {};
    municipalities: Array<{disabled: boolean, id: number, name: string}> = [];

    $refs!: {
        address: HTMLElement;
        addressMunicipality: HTMLElement;
    };

    @Watch('address.province')
    async onProvinceChange(newVal, oldVal) {
        if (newVal === oldVal) {
            return;
        }

        if (newVal === null) {
            this.address.municipality = -1;
            return;
        }

        await this.getMunicipalities();
    }

    get addressFieldMappings() {
        return {
            COUNTRY: 'country',
            PROVINCE: 'province',
            FOREIGN_PROVINCE: 'foreignProvince',
            MUNICIPALITY: 'municipality',
            FOREIGN_MUNICIPALITY: 'foreignMunicipality',
            STREET_TYPE: 'streetType',
            STREET_NAME: 'streetName',
            NUMBER: 'number',
            PORTAL: 'portal',
            BLOCK: 'block',
            STAIR: 'stair',
            FLOOR: 'floor',
            DOOR: 'door',
            POST_CODE: 'postCode',
            FOREIGN_POST_CODE: 'foreignPostCode'
        };
    }

    get isMunicipalitySetted() {
        return this.address.municipality !== null && this.address.municipality > -1;
    }

    setEditMode() {
        this.$emit('setEditMode', InscriptionModules.enum.ADDRESS);
    }

    onSaveEditedAddress() {
        this.$emit('edit', InscriptionModules.enum.ADDRESS, this.$refs.address);
    }

    async cancelEditMode() {
        this.$emit('cancelEdit', InscriptionModules.enum.ADDRESS);
        await this.getMunicipalities();
    }

    onChangeAddress(field: string, value: any) {
        this.$emit('update:address', new Address(
            {
                ...this.address.toServerInscription(),
                [field]: value
            }));
    }

    async getCountries() {
        try {
            const response = await MasterDataService.getCountriesAsync();
            this.countries = response.map(element => ({ name: element.value, id: element.key }));
        } catch (error) {
            console.error(error);
        }
    }

    async getProvinces() {
        try {
            this.provinces = await MasterDataService.getProvincesAsync();
        } catch (error) {
            console.error(error);
        }
    }

    async getMunicipalities() {
        if (this.address.province === null) { return; }
        this.resetMunicipalities();
        try {
            const response = await MasterDataService.getMunicipalitiesAsync(Number(this.address.province));
            response.forEach(municipality => {
                if (!municipality.isObsolete || this.address.municipality === municipality.municipalityCode) {
                    this.municipalities.push({ name: municipality.municipalityName, id: municipality.municipalityCode, disabled: municipality.isObsolete });
                }
            });
            this.municipalities = this.municipalities.sort((a, b) => a.name.localeCompare(b.name));
        } catch (error) {
            console.log(error);
        }
    }

    setMunicipalityOnZero() {
        this.onChangeAddress(this.addressFieldMappings.MUNICIPALITY, -1);
    }

    async manageMunicipalities() {
        await this.getMunicipalities();
    }

    resetMunicipalities() {
        this.municipalities = [];
        if (this.isMunicipalitySetted) { return; } 
        this.address.municipality = -1;
    }

    async getAddressMasterData() {
        try {
            await this.getCountries();
            await this.getProvinces();
            await this.getMunicipalities();
        } catch (error) {
            console.log(error);
        }
    }

    async created() {
        await this.getAddressMasterData();
    }
}
